/**
 * @ComponentFor RowLayoutBlockViewModel
 */
import React, { ReactElement } from 'react';
import { EpiProperty, supportsComponentProp, ComponentType } from '@polarnopyret/scope-episerver';
import RowLayoutBlockType from './RowLayoutBlockViewModel.type';
import { styled } from '@glitz/react';
import connect from 'Shared/connect';
import { RecursivePartial } from 'Shared/utils';
import CTABlockViewModel from 'Shared/Blocks/CTABlock/CTABlockViewModel.type';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import * as style from 'Shared/Style';

type RowLayoutContentAreaPropType = {
  elementProps: React.HTMLAttributes<HTMLElement> & {
    children?: React.ReactNode;
  };
  componentProps: {
    component?: ComponentType;
  };
};

const RowLayoutContentArea = ({
  elementProps,
  componentProps: { component: Component = 'div', ...componentProps },
}: RowLayoutContentAreaPropType) => {
  
  if (
    (elementProps.children as Array<ReactElement>)
      ?.map((child) => child.props?.item?.componentName)
      ?.find((name) => name.endsWith('CTABlockViewModel'))
  ) {
    return (
      <Component {...elementProps}>
        <RowLayoutContentAreaForCTA>{elementProps.children}</RowLayoutContentAreaForCTA>
      </Component>
    );
  }

  return (
    <Component {...elementProps}>
      <RowLayoutContentAreaDefault>{elementProps.children}</RowLayoutContentAreaDefault>
    </Component>
  );
};

const Container = styled.div({
  flexGrow: 1,
  borderRadius: '8px',
  overflow: 'hidden',
})

const GridWrapper = styled(GridWrap, {
  padding: { y: '80px' },
  ['@media ' + style.mediaMinLarge]: {
    padding: { y: '120px' }
  },
})

const RowLayoutContentAreaDefault = connect((state) => {
  return {
    currentBreakpoint: state.currentBreakpoint,
  };
})((props: RecursivePartial<any> & { currentBreakpoint: number; children: any }) => {
  return (
    <GridWrapper className="grid wsb">
      {props.children?.map(
        (e: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal, i: React.Key, a: string | any[]) => (
          <Container key={i} className={createGridClasses(12, 6,)}>
            {e}
          </Container>
        ),
      )}
    </GridWrapper>
  );
});

const RowLayoutContentAreaForCTA = connect((state) => {
  return {
    currentBreakpoint: state.currentBreakpoint,
  };
})((props: RecursivePartial<CTABlockViewModel> & { currentBreakpoint: number; children: any }) => {  
  return (
    <GridWrapper className="grid wsb">
      {props.children?.map(
        (e: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal, i: React.Key, a: string | any[]) => (
          <Container
            className={createGridClasses(
              12,
              5,
              (typeof i === 'string' ? parseInt(i) : i) % 2 ? createOffsetGridClassLarge(7) : createOffsetGridClassLarge(2),
            )}
            key={i}
          >
            {e}
          </Container>
        ),
      )}
    </GridWrapper>
  );
});

type PropType = RowLayoutBlockType & {
  component?: ComponentType;
};

export default supportsComponentProp(
  class RowLayoutBlock extends React.Component<PropType> {
    elementComponent: React.StatelessComponent<React.HTMLAttributes<HTMLElement>> = (props) => (
      <RowLayoutContentArea elementProps={props} componentProps={this.props} />
    );
    render() {
      return <EpiProperty className='block rowlayout' for={this.props.block.items} component={this.elementComponent} />;
    }
  },
);
